/**
 * AdminLeaguePage.jsx
 * Admin interface for managing all three league tiers.
 */
import { useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import {
  doc, getDoc, setDoc, collection, addDoc,
  query, orderBy, limit, getDocs, serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../../services/firebase';
import './AdminLeaguePage.css';

const fnRegion = getFunctions(undefined, 'australia-southeast1');

// ── Constants ──────────────────────────────────────────────────────────────────

const LEAGUE_ORDER = ['lonhro', 'so_you_think', 'black_caviar'];

const LEAGUE_META = {
  lonhro:       { label: 'Lonhro League',        colour: '#A0522D', emoji: '🥉', tier: 3 },
  so_you_think: { label: 'So You Think League',  colour: '#7B8FA1', emoji: '🥈', tier: 2 },
  black_caviar: { label: 'Black Caviar League',  colour: '#C9A84C', emoji: '🏆', tier: 1 },
};

const POS_LABELS = {
  1: '1st', 2: '2nd', 3: '3rd', 4: '4th',  5: '5th',
  6: '6th', 7: '7th', 8: '8th', 9: '9th', 10: '10th',
};

const DEFAULT_POINTS_SCALE = {
  1: 1000, 2: 980,  3: 960,  4: 940,  5: 920,
  6: 900,  7: 880,  8: 860,  9: 840,  10: 820,
  11: 805, 12: 790, 13: 775, 14: 760, 15: 745,
  16: 730, 17: 715, 18: 700, 19: 685, 20: 670,
  21: 655, 22: 640, 23: 625, 24: 610, 25: 595,
  26: 580, 27: 565, 28: 550, 29: 535, 30: 520,
  31: 510, 32: 500, 33: 490, 34: 480, 35: 470,
  36: 460, 37: 450, 38: 440, 39: 430, 40: 420,
  41: 410, 42: 400, 43: 390, 44: 380, 45: 370,
  46: 360, 47: 350, 48: 340, 49: 330, 50: 320,
  51: 310, 52: 300, 53: 290, 54: 280, 55: 270,
  56: 260, 57: 250, 58: 240, 59: 230, 60: 220,
  61: 215, 62: 210, 63: 205, 64: 200, 65: 195,
  66: 190, 67: 185, 68: 180, 69: 175, 70: 170,
  71: 165, 72: 160, 73: 155, 74: 150, 75: 145,
  76: 140, 77: 135, 78: 130, 79: 125, 80: 120,
  81: 115, 82: 110, 83: 105, 84: 100, 85: 95,
  86: 90,  87: 85,  88: 80,  89: 75,  90: 70,
  91: 65,  92: 60,  93: 55,  94: 50,  95: 45,
  96: 40,  97: 35,  98: 30,  99: 25,  100: 20,
  default: 0,
};

function blankLeague(id) {
  const defaults = {
    lonhro:       { maxPlayers: null, promoteCount: 1000, demoteCount: 0,   prizePool: 5000,  prizes: { 1:1500,2:900,3:700,4:500,5:400,6:300,7:250,8:200,9:150,10:100 } },
    so_you_think: { maxPlayers: 5000, promoteCount: 100,  demoteCount: 1000, prizePool: 20000, prizes: { 1:6000,2:3500,3:2500,4:2000,5:1500,6:1300,7:1100,8:900,9:700,10:500 } },
    black_caviar: { maxPlayers: 500,  promoteCount: 0,    demoteCount: 100,  prizePool: 75000, prizes: { 1:25000,2:15000,3:10000,4:7500,5:5000,6:3500,7:3000,8:2500,9:2000,10:1500 } },
  }[id];

  return {
    name:                LEAGUE_META[id].label,
    enabled:             true,
    status:              'off_season',
    playerCount:         0,
    maxPlayers:          defaults.maxPlayers,
    unlimitedPlayers:    defaults.maxPlayers === null,
    season1Name:         'Autumn Season',
    season1Start:        '2027-02-01',
    season1End:          '2027-05-31',
    season2Name:         'Spring Season',
    season2Start:        '2026-08-01',
    season2End:          '2026-11-30',
    sponsor:             '',
    sponsorExpiry:       '',
    promoteCount:        defaults.promoteCount,
    demoteCount:         defaults.demoteCount,
    directToTop:         0,
    minShowdowns:        20,
    prizePool:           defaults.prizePool,
    prizes:              { ...defaults.prizes },
    gamesPerWeekSeason:  6,
    gamesPerWeekOffSeason: 3,
    offSeasonPrizePool:  0,
    offSeasonFormat:     '',
    entriesLocked:       false,
    pointsScale:         DEFAULT_POINTS_SCALE,
  };
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function prizeSum(prizes) {
  return Object.values(prizes).reduce((a, v) => a + (Number(v) || 0), 0);
}

function describeChanges(before, after) {
  const changes = [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  for (const k of keys) {
    if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) {
      changes.push(`${k}: ${JSON.stringify(before[k])} → ${JSON.stringify(after[k])}`);
    }
  }
  return changes.join('; ') || 'No changes detected';
}

function formatTs(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-AU', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// ── Main page component ────────────────────────────────────────────────────────

export function AdminLeaguePage() {
  const [currentUser,   setCurrentUser]   = useState(null);
  const [leagueData,    setLeagueData]    = useState({});
  const [originalData,  setOriginalData]  = useState({});
  const [saving,        setSaving]        = useState({});
  const [saveMsg,       setSaveMsg]       = useState({});
  const [auditLogs,     setAuditLogs]     = useState({});
  const [auditOpen,     setAuditOpen]     = useState({});
  const [previewData,   setPreviewData]   = useState({});
  const [previewRun,    setPreviewRun]    = useState(new Set());
  const [editPoints,    setEditPoints]    = useState(false);
  const [backfilling,   setBackfilling]   = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  // Auth
  useEffect(() => {
    return onAuthStateChanged(auth, u => setCurrentUser(u));
  }, []);

  // Load league data from Firestore
  const loadLeagues = useCallback(async () => {
    const loaded = {};
    const originals = {};
    for (const id of LEAGUE_ORDER) {
      try {
        const snap = await getDoc(doc(db, 'leagues', id));
        if (snap.exists()) {
          const data = snap.data();
          const rawScale = data.pointsScale ?? DEFAULT_POINTS_SCALE;
          const normalisedScale = Object.fromEntries(
            Object.entries(rawScale).map(([k, v]) => [
              k === 'default' ? 'default' : parseInt(k),
              parseInt(v),
            ])
          );
          const merged = {
            ...blankLeague(id),
            ...data,
            pointsScale: normalisedScale,
          };
          merged.unlimitedPlayers = merged.maxPlayers === null;
          loaded[id]    = merged;
          originals[id] = { ...merged };
        } else {
          loaded[id]    = blankLeague(id);
          originals[id] = blankLeague(id);
        }
      } catch {
        loaded[id]    = blankLeague(id);
        originals[id] = blankLeague(id);
      }
    }
    setLeagueData(loaded);
    setOriginalData(originals);
  }, []);

  useEffect(() => {
    loadLeagues();
  }, [loadLeagues]);

  // Load audit log for a league
  const loadAudit = useCallback(async (leagueId) => {
    try {
      const q = query(
        collection(db, 'league_audit_log', leagueId, 'entries'),
        orderBy('changedAt', 'desc'),
        limit(20),
      );
      const snap = await getDocs(q);
      setAuditLogs(prev => ({
        ...prev,
        [leagueId]: snap.docs.map(d => ({ id: d.id, ...d.data() })),
      }));
    } catch {
      setAuditLogs(prev => ({ ...prev, [leagueId]: [] }));
    }
  }, []);

  // Update a field for a league
  const update = (leagueId, field, value) => {
    setLeagueData(prev => ({
      ...prev,
      [leagueId]: { ...prev[leagueId], [field]: value },
    }));
  };

  const updatePrize = (leagueId, pos, value) => {
    setLeagueData(prev => ({
      ...prev,
      [leagueId]: {
        ...prev[leagueId],
        prizes: { ...prev[leagueId].prizes, [pos]: Number(value) || 0 },
      },
    }));
  };

  // Save a league
  const handleSave = async (leagueId) => {
    if (!currentUser) return;
    setSaving(p => ({ ...p, [leagueId]: true }));
    const data = { ...leagueData[leagueId] };
    if (data.unlimitedPlayers) data.maxPlayers = null;
    try {
      await setDoc(doc(db, 'leagues', leagueId), data, { merge: true });
      // Audit
      const changes = describeChanges(originalData[leagueId] || {}, data);
      await addDoc(collection(db, 'league_audit_log', leagueId, 'entries'), {
        changedAt:  serverTimestamp(),
        changedBy:  currentUser.email || currentUser.uid,
        changes,
      });
      setOriginalData(p => ({ ...p, [leagueId]: { ...data } }));
      setSaveMsg(p => ({ ...p, [leagueId]: 'saved' }));
      setTimeout(() => setSaveMsg(p => ({ ...p, [leagueId]: null })), 2500);
      // Refresh audit
      if (auditOpen[leagueId]) loadAudit(leagueId);
    } catch (err) {
      setSaveMsg(p => ({ ...p, [leagueId]: 'error' }));
      setTimeout(() => setSaveMsg(p => ({ ...p, [leagueId]: null })), 3000);
      console.error('[AdminLeaguePage] save error:', err);
    } finally {
      setSaving(p => ({ ...p, [leagueId]: false }));
    }
  };

  const handlePreview = (leagueId) => {
    const d = leagueData[leagueId];
    const lines = [];
    if (d.promoteCount > 0) lines.push(`▲ Top ${d.promoteCount} players promoted out of ${leagueId}`);
    if (d.directToTop  > 0) lines.push(`▲▲ Top ${d.directToTop} players promoted directly to Black Caviar`);
    if (d.demoteCount  > 0) lines.push(`▼ Bottom ${d.demoteCount} players demoted from ${leagueId}`);
    if (!lines.length) lines.push('No promotion or demotion configured for this league.');
    setPreviewData(p => ({ ...p, [leagueId]: lines }));
    setPreviewRun(p => new Set([...p, leagueId]));
  };

  const handleForceOpen = (leagueId) => {
    if (!window.confirm(`Force-open season for ${LEAGUE_META[leagueId].label}?\nThis will set status to "active".`)) return;
    update(leagueId, 'status', 'active');
  };

  const handleForceClose = (leagueId) => {
    if (!window.confirm(`Force-close season for ${LEAGUE_META[leagueId].label}?\nThis will set status to "finalising".`)) return;
    update(leagueId, 'status', 'finalising');
  };

  const handleProcessPromotions = (leagueId) => {
    if (!previewRun.has(leagueId)) return;
    if (!window.confirm('Process promotions & demotions?\n\nThis cannot be undone.')) return;
    alert('Promotion processing would be triggered here via the processSeasonEnd Cloud Function.');
  };

  const handleBackfill = async () => {
    if (!window.confirm(
      'This will award season points for all finalised games that haven\'t been scored yet.\n\n' +
      'This is safe to run multiple times — already-scored games are skipped automatically.\n\n' +
      'Continue?'
    )) return;

    setBackfilling(true);
    setBackfillResult(null);
    try {
      const fn     = httpsCallable(fnRegion, 'backfillSeasonPoints');
      const result = await fn({});
      setBackfillResult({ ok: true, ...result.data });
    } catch (err) {
      setBackfillResult({ ok: false, error: err.message });
    } finally {
      setBackfilling(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="al-page">
      <h1 className="al-page-title">League Management</h1>
      <p className="al-page-subtitle">
        Configure all three league tiers — promotion thresholds, prize money, and season schedule.
      </p>

      {/* ── Season Points Backfill ── */}
      <div style={{
        background: '#0F1D2E',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 10,
        padding: '16px 20px',
        marginBottom: 24,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#E2E8F0', marginBottom: 4 }}>
            🔁 Backfill Season Points
          </div>
          <div style={{ fontSize: 12, color: '#94A3B8', lineHeight: 1.5 }}>
            Award season points for all finalised games that were never scored.
            Already-scored games are skipped — safe to run multiple times.
          </div>
          {backfillResult && (
            <div style={{
              marginTop: 10,
              padding: '10px 14px',
              borderRadius: 8,
              background: backfillResult.ok ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${backfillResult.ok ? '#22c55e' : '#ef4444'}`,
              fontSize: 12,
              color: backfillResult.ok ? '#86efac' : '#fca5a5',
            }}>
              {backfillResult.ok ? (
                <>
                  <div>✓ <strong>{backfillResult.processed}</strong> game{backfillResult.processed !== 1 ? 's' : ''} scored
                    {' '}· <strong>{backfillResult.alreadyDone}</strong> already done
                    {backfillResult.errors?.length > 0 && (
                      <> · <strong style={{ color: '#fca5a5' }}>{backfillResult.errors.length} error{backfillResult.errors.length !== 1 ? 's' : ''}</strong></>
                    )}
                  </div>
                  <div style={{ color: '#64748B', marginTop: 4 }}>Season: {backfillResult.seasonId}</div>
                  {backfillResult.detail?.length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      {backfillResult.detail.map(d => (
                        <div key={d.slateId} style={{ color: '#94A3B8' }}>
                          • {d.slateId}: {d.awarded} player{d.awarded !== 1 ? 's' : ''}
                        </div>
                      ))}
                    </div>
                  )}
                  {backfillResult.errors?.map(e => (
                    <div key={e.slateId} style={{ color: '#fca5a5', marginTop: 2 }}>
                      ✗ {e.slateId}: {e.error}
                    </div>
                  ))}
                </>
              ) : (
                <div>✗ {backfillResult.error}</div>
              )}
            </div>
          )}
        </div>
        <button
          onClick={handleBackfill}
          disabled={backfilling}
          style={{
            padding: '8px 20px',
            background: backfilling ? '#334155' : '#1D4ED8',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: backfilling ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
            alignSelf: 'center',
          }}
        >
          {backfilling ? '⏳ Running…' : 'Run Backfill'}
        </button>
      </div>

      {LEAGUE_ORDER.map(leagueId => {
        const meta = LEAGUE_META[leagueId];
        const d    = leagueData[leagueId];
        if (!d) return <div key={leagueId} style={{ color: '#64748B', padding: 16 }}>Loading {meta.label}…</div>;

        const sum       = prizeSum(d.prizes);
        const pool      = Number(d.prizePool) || 0;
        const sumClass  = sum === pool ? 'al-prize-sum--ok' : sum > pool ? 'al-prize-sum--over' : 'al-prize-sum--under';
        const isSaving  = saving[leagueId];
        const msg       = saveMsg[leagueId];

        return (
          <div
            key={leagueId}
            className="al-card"
            style={{ borderLeft: `5px solid ${meta.colour}` }}
          >
            {/* ── Card header ── */}
            <div className="al-card-header">
              <span style={{ fontSize: 22 }}>{meta.emoji}</span>
              <input
                className="al-card-title-input"
                value={d.name}
                onChange={e => update(leagueId, 'name', e.target.value)}
              />
              <label className="al-toggle-wrap">
                <span className="al-toggle-label">{d.enabled ? 'ENABLED' : 'DISABLED'}</span>
                <span className="al-toggle">
                  <input
                    type="checkbox"
                    checked={d.enabled}
                    onChange={e => update(leagueId, 'enabled', e.target.checked)}
                  />
                  <span className="al-toggle-slider" />
                </span>
              </label>
              <span className={`al-status-badge al-status-badge--${d.status}`}>
                {d.status.replace('_', ' ').toUpperCase()}
              </span>
              <span className="al-player-count">
                {d.playerCount || 0} / {d.unlimitedPlayers ? '∞' : (d.maxPlayers || '?')} players
              </span>
            </div>

            {/* ── SECTION 1 — Configuration ── */}
            <div className="al-section">
              <div className="al-section-title">Configuration</div>
              <div className="al-grid-2">
                <div className="al-field">
                  <label>Player Cap</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input
                      className="al-input"
                      type="number"
                      min={1}
                      value={d.unlimitedPlayers ? '' : (d.maxPlayers || '')}
                      placeholder={d.unlimitedPlayers ? 'Unlimited' : ''}
                      disabled={d.unlimitedPlayers}
                      onChange={e => update(leagueId, 'maxPlayers', Number(e.target.value))}
                      style={{ flex: 1 }}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748B', whiteSpace: 'nowrap' }}>
                      <input
                        type="checkbox"
                        checked={!!d.unlimitedPlayers}
                        onChange={e => update(leagueId, 'unlimitedPlayers', e.target.checked)}
                      />
                      Unlimited
                    </label>
                  </div>
                </div>
                <div className="al-field">
                  <label>Naming Rights Sponsor</label>
                  <input className="al-input" value={d.sponsor} onChange={e => update(leagueId, 'sponsor', e.target.value)} placeholder="Optional" />
                </div>
                <div className="al-field">
                  <label>Sponsor Rights Expiry</label>
                  <input className="al-input" type="date" value={d.sponsorExpiry} onChange={e => update(leagueId, 'sponsorExpiry', e.target.value)} />
                </div>
              </div>
              <div className="al-grid-3" style={{ marginTop: 16 }}>
                <div className="al-field">
                  <label>Season 1 Name</label>
                  <input className="al-input" value={d.season1Name} onChange={e => update(leagueId, 'season1Name', e.target.value)} />
                </div>
                <div className="al-field">
                  <label>Season 1 Start</label>
                  <input className="al-input" type="date" value={d.season1Start} onChange={e => update(leagueId, 'season1Start', e.target.value)} />
                </div>
                <div className="al-field">
                  <label>Season 1 End</label>
                  <input className="al-input" type="date" value={d.season1End} onChange={e => update(leagueId, 'season1End', e.target.value)} />
                </div>
                <div className="al-field">
                  <label>Season 2 Name</label>
                  <input className="al-input" value={d.season2Name} onChange={e => update(leagueId, 'season2Name', e.target.value)} />
                </div>
                <div className="al-field">
                  <label>Season 2 Start</label>
                  <input className="al-input" type="date" value={d.season2Start} onChange={e => update(leagueId, 'season2Start', e.target.value)} />
                </div>
                <div className="al-field">
                  <label>Season 2 End</label>
                  <input className="al-input" type="date" value={d.season2End} onChange={e => update(leagueId, 'season2End', e.target.value)} />
                </div>
              </div>
            </div>

            {/* ── SECTION 2 — Promotion & Demotion ── */}
            <div className="al-section">
              <div className="al-section-title">Promotion &amp; Demotion</div>
              <div className="al-grid-2">
                {leagueId === 'lonhro' && (<>
                  <div className="al-field">
                    <label>Promote to So You Think</label>
                    <input className="al-input" type="number" min={0} value={d.promoteCount}
                      onChange={e => update(leagueId, 'promoteCount', Number(e.target.value))} />
                    <span className="al-hint">Positions 1 to {d.promoteCount || '?'} move up</span>
                  </div>
                  <div className="al-field">
                    <label>Direct to Black Caviar (top X)</label>
                    <input className="al-input" type="number" min={0} value={d.directToTop}
                      onChange={e => update(leagueId, 'directToTop', Number(e.target.value))} />
                    <span className="al-hint">Top {d.directToTop || 0} skip So You Think entirely</span>
                  </div>
                </>)}

                {leagueId === 'so_you_think' && (<>
                  <div className="al-field">
                    <label>Promote to Black Caviar</label>
                    <input className="al-input" type="number" min={0} value={d.promoteCount}
                      onChange={e => update(leagueId, 'promoteCount', Number(e.target.value))} />
                    <span className="al-hint">Top {d.promoteCount || '?'} players promoted</span>
                  </div>
                  <div className="al-field">
                    <label>Demote to Lonhro</label>
                    <input className="al-input" type="number" min={0} value={d.demoteCount}
                      onChange={e => update(leagueId, 'demoteCount', Number(e.target.value))} />
                    <span className="al-hint">Bottom {d.demoteCount || '?'} players demoted</span>
                  </div>
                  <div className="al-field">
                    <label>Receive from Lonhro (display only)</label>
                    <input className="al-input" value={leagueData['lonhro']?.promoteCount || 0} readOnly
                      style={{ background: '#F8FAFC', color: '#94A3B8' }} />
                    <span className="al-hint">Set by Lonhro promote count above</span>
                  </div>
                </>)}

                {leagueId === 'black_caviar' && (<>
                  <div className="al-field">
                    <label>Demote to So You Think</label>
                    <input className="al-input" type="number" min={0} value={d.demoteCount}
                      onChange={e => update(leagueId, 'demoteCount', Number(e.target.value))} />
                    <span className="al-hint">Bottom {d.demoteCount || '?'} players demoted</span>
                  </div>
                  <div className="al-field">
                    <label>Of those, send to Lonhro</label>
                    <input className="al-input" type="number" min={0} value={d.directToTop || 0}
                      onChange={e => update(leagueId, 'directToTop', Number(e.target.value))} />
                    <span className="al-hint">Bottom {d.directToTop || 0} of demoted group go to Lonhro</span>
                  </div>
                  <div className="al-field" style={{ gridColumn: '1/-1' }}>
                    <label>Receive from So You Think (display only)</label>
                    <input className="al-input" value={leagueData['so_you_think']?.promoteCount || 0} readOnly
                      style={{ background: '#F8FAFC', color: '#94A3B8' }} />
                  </div>
                  <div className="al-field" style={{ gridColumn: '1/-1' }}>
                    <div style={{ fontSize: 12, color: '#64748B', background: '#F8FAFC', borderRadius: 8, padding: '8px 12px' }}>
                      ℹ️ Total demoted = So You Think demotions + Lonhro demotions combined.
                    </div>
                  </div>
                </>)}
              </div>
            </div>

            {/* ── SECTION 3 — Prize Money ── */}
            <div className="al-section">
              <div className="al-section-title">Prize Money</div>
              <div className="al-grid-2" style={{ marginBottom: 20 }}>
                <div className="al-field">
                  <label>Total Prize Pool ($)</label>
                  <input className="al-input" type="number" min={0} value={d.prizePool}
                    onChange={e => update(leagueId, 'prizePool', Number(e.target.value))} />
                </div>
              </div>
              <table className="al-prize-table">
                <thead>
                  <tr>
                    <th className="al-prize-pos">Position</th>
                    <th>Prize ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {[1,2,3,4,5,6,7,8,9,10].map(pos => (
                    <tr key={pos}>
                      <td className="al-prize-pos">{POS_LABELS[pos]}</td>
                      <td>
                        <input
                          className="al-input"
                          type="number"
                          min={0}
                          value={d.prizes[pos] ?? 0}
                          onChange={e => updatePrize(leagueId, pos, e.target.value)}
                          style={{ width: 160 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className={`al-prize-sum ${sumClass}`}>
                <span>Prize total: ${sum.toLocaleString()}</span>
                <span style={{ opacity: 0.6, fontSize: 12 }}>
                  {sum === pool ? '✓ Matches prize pool' :
                   sum > pool  ? `⚠ Over by $${(sum - pool).toLocaleString()}` :
                                 `⚠ Under by $${(pool - sum).toLocaleString()}`}
                </span>
              </div>
            </div>

            {/* ── SECTION 4 — Rules ── */}
            <div className="al-section">
              <div className="al-section-title">Rules</div>
              <div className="al-grid-3">
                <div className="al-field">
                  <label>Min showdowns to qualify</label>
                  <input className="al-input" type="number" min={0} value={d.minShowdowns}
                    onChange={e => update(leagueId, 'minShowdowns', Number(e.target.value))} />
                </div>
                <div className="al-field">
                  <label>Games/week (in season)</label>
                  <input className="al-input" type="number" min={0} value={d.gamesPerWeekSeason}
                    onChange={e => update(leagueId, 'gamesPerWeekSeason', Number(e.target.value))} />
                </div>
                <div className="al-field">
                  <label>Games/week (off season)</label>
                  <input className="al-input" type="number" min={0} value={d.gamesPerWeekOffSeason}
                    onChange={e => update(leagueId, 'gamesPerWeekOffSeason', Number(e.target.value))} />
                </div>
                <div className="al-field">
                  <label>Off-season prize pool ($)</label>
                  <input className="al-input" type="number" min={0} value={d.offSeasonPrizePool}
                    onChange={e => update(leagueId, 'offSeasonPrizePool', Number(e.target.value))} />
                </div>
              </div>
              <div className="al-field" style={{ marginTop: 16 }}>
                <label>Off-season format description</label>
                <textarea className="al-textarea" value={d.offSeasonFormat}
                  onChange={e => update(leagueId, 'offSeasonFormat', e.target.value)}
                  placeholder="Describe the off-season format, prizes, and rules…" />
              </div>

              {/* Points scale — reads exclusively from d.pointsScale (per-league Firestore data) */}
              {(() => {
                const scale = d.pointsScale ?? DEFAULT_POINTS_SCALE;
                const setScale = (updater) => {
                  const next = typeof updater === 'function' ? updater(scale) : updater;
                  update(leagueId, 'pointsScale', next);
                };
                // Numeric positions 1–100 in strict ascending order
                const positions = Array.from({ length: 100 }, (_, i) => i + 1);
                return (
                  <div style={{ marginTop: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#24344E', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Points Scale
                      </span>
                      <button className="al-collapse-btn" onClick={() => setEditPoints(p => !p)}>
                        {editPoints ? '▲ Collapse' : '▼ Edit Points Scale'}
                      </button>
                    </div>
                    {!editPoints ? (
                      <div className="al-points-readonly">
                        {[1,2,3,4,5,6,7,8,9,10].map(p => (
                          <span key={p} className="al-points-pill">{POS_LABELS[p]} = {scale[p] ?? '—'}</span>
                        ))}
                        <span className="al-points-pill">101+ = {scale.default ?? 0}</span>
                        <span style={{ fontSize: 11, color: '#94A3B8', alignSelf: 'center' }}>
                          …100 positions total
                        </span>
                      </div>
                    ) : (
                      <div className="al-points-editor">
                        {positions.map(pos => (
                          <div key={pos} className="al-field">
                            <label>{POS_LABELS[pos] || `${pos}th`}</label>
                            <input
                              className="al-input"
                              type="number"
                              min={0}
                              value={scale[pos] ?? ''}
                              onChange={e => setScale(prev => ({ ...prev, [pos]: Number(e.target.value) }))}
                            />
                          </div>
                        ))}
                        <div className="al-field">
                          <label>101st+</label>
                          <input
                            className="al-input"
                            type="number"
                            min={0}
                            value={scale.default ?? 0}
                            onChange={e => setScale(prev => ({ ...prev, default: Number(e.target.value) }))}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* ── SECTION 5 — Actions ── */}
            <div className="al-actions">
              <button
                className="al-btn al-btn--primary"
                onClick={() => handleSave(leagueId)}
                disabled={isSaving}
              >
                {isSaving ? <><span className="al-spinner" /> Saving…</> :
                 msg === 'saved' ? '✓ Saved' :
                 msg === 'error' ? '✗ Error' :
                 'Save Changes'}
              </button>

              <button className="al-btn al-btn--orange"
                onClick={() => handleForceOpen(leagueId)}>
                Force Open Season
              </button>
              <button className="al-btn al-btn--orange"
                onClick={() => handleForceClose(leagueId)}>
                Force Close Season
              </button>

              <button className="al-btn al-btn--grey"
                onClick={() => handlePreview(leagueId)}>
                Preview Promotions
              </button>
              <button
                className="al-btn al-btn--red"
                disabled={!previewRun.has(leagueId)}
                title={!previewRun.has(leagueId) ? 'Run Preview Promotions first' : ''}
                onClick={() => handleProcessPromotions(leagueId)}
              >
                Process Promotions
              </button>

              <label className="al-toggle-wrap" style={{ marginLeft: 'auto' }}>
                <span className="al-toggle-label" style={{ fontSize: 12, color: '#64748B' }}>
                  {d.entriesLocked ? 'ENTRIES LOCKED' : 'ENTRIES OPEN'}
                </span>
                <span className="al-toggle">
                  <input type="checkbox" checked={!!d.entriesLocked}
                    onChange={e => update(leagueId, 'entriesLocked', e.target.checked)} />
                  <span className="al-toggle-slider" />
                </span>
              </label>
            </div>

            {/* Preview panel */}
            {previewData[leagueId] && (
              <div className="al-section" style={{ paddingTop: 0 }}>
                <div className="al-preview-panel">
                  <h4>Promotion Preview — {meta.label}</h4>
                  {previewData[leagueId].map((line, i) => (
                    <div key={i} style={{ marginBottom: 4 }}>{line}</div>
                  ))}
                  <div style={{ marginTop: 10, fontSize: 11, color: '#94A3B8' }}>
                    This is a preview only. Click "Process Promotions" to execute.
                  </div>
                </div>
              </div>
            )}

            {/* ── SECTION 6 — Audit Log ── */}
            <div className="al-section">
              <button
                className="al-audit-toggle"
                onClick={() => {
                  const nowOpen = !auditOpen[leagueId];
                  setAuditOpen(p => ({ ...p, [leagueId]: nowOpen }));
                  if (nowOpen && !auditLogs[leagueId]) loadAudit(leagueId);
                }}
              >
                {auditOpen[leagueId] ? '▲' : '▼'} Audit Log
              </button>
              {auditOpen[leagueId] && (
                <div className="al-audit-entries">
                  {!auditLogs[leagueId] ? (
                    <div style={{ color: '#94A3B8', fontSize: 12 }}>Loading…</div>
                  ) : auditLogs[leagueId].length === 0 ? (
                    <div style={{ color: '#94A3B8', fontSize: 12 }}>No audit entries yet.</div>
                  ) : auditLogs[leagueId].map(entry => (
                    <div key={entry.id} className="al-audit-entry">
                      <div className="al-audit-entry-meta">
                        {formatTs(entry.changedAt)} — {entry.changedBy}
                      </div>
                      <div>{entry.changes}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default AdminLeaguePage;
